import { trackEvent } from '@/utils/analytics'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { apiService } from '../service/api'
import { useUIv2 } from '@/hooks/useUIv2'
import DocumentPreviewV2 from '@/routes/DataCatalog/DataCatalogV2/DocumentPreviewV2'
import BoardLayout from '@/components/layout/BoardLayout'
import { PdfJsViewer } from '@/components/PdfViewer/PdfJsViewer'
import AdBlockerWarning from '@/components/AdBlockerWarning'
import FileNameWithCopy from '@/components/FileNameWithCopy'
import { useRenameDocument } from '@/hooks/useRenameDocument'
import { formatPageText } from '@/utils/pageTextUtils'
import { useTranslation } from 'react-i18next'
import type { PageClassification } from '@/types/mixed-documents'
import { pnidPagesParamFor } from '@/utils/resolveGraphOpen'
import { DocumentTabs, type DocumentTab } from '@/components/DocumentViewer/DocumentTabs'
import { ReviewTab } from '@/components/DocumentViewer/ReviewTab'
import { ExtractionsTab } from '@/components/DocumentViewer/ExtractionsTab'
import { RawTab } from '@/components/DocumentViewer/RawTab'
import { SummaryTab } from '@/components/DocumentViewer/SummaryTab'
import { DetailsTab } from '@/components/DocumentViewer/DetailsTab'
import { LinesTab } from '@/components/DocumentViewer/LinesTab'
import { IO_LINE_TRACING_ENABLED } from '@/config/ioLineTracing'
import { getUserDisplayName } from '@/utils/userUtils'
import { useAppStore } from '@/store'
import { X } from 'lucide-react'
import { enableFabricEmbedMode, isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import { cleanDocumentName } from './IntelligentBoard/utils/stripGraphPrefix'

// Cache size limit for page text (text is smaller than PDF pages, allow more)
const MAX_TEXT_CACHE_SIZE = 20

interface DocumentMetadata {
  uploadedBy?: string
  uploadDate?: string
  lastModified?: string
  fileSize?: string
  documentType?: string
  category?: string
  extractionStatus?: string
  extractionDate?: string
}

// Helper function to extract file extension
const getFileExtension = (filename: string): string => {
  const ext = filename.split('.').pop()
  return ext || 'file'
}

export default function PdfViewer() {
  const isV2 = useUIv2()
  return isV2 ? <DocumentPreviewV2 /> : <PdfViewerLegacy />
}

function PdfViewerLegacy() {
  const { t } = useTranslation(['board', 'data_catalog', 'document_viewer'])
  const { doc_id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const navigateBack = useNavigateBack()
  const { user, allowedDocumentIds } = useAppStore()

  // Fabric embed mode: detect from URL query parameter
  const isEmbedMode = searchParams.get('embed') === 'fabric'
  useEffect(() => {
    if (isEmbedMode && !isFabricEmbedMode()) {
      enableFabricEmbedMode()
    }
  }, [isEmbedMode])

  const [currentPage, setCurrentPage] = useState(() => {
    const pageParam = searchParams.get('page')
    return pageParam ? parseInt(pageParam, 10) : 1
  })

  // Sync currentPage with URL searchParams when navigating back from graph editor
  useEffect(() => {
    const pageParam = searchParams.get('page')
    if (pageParam) {
      const pageNumber = parseInt(pageParam, 10)
      if (pageNumber !== currentPage) {
        setCurrentPage(pageNumber)
      }
    }
  }, [searchParams])

  // boardName comes only from the ?boardName= query param, which is set when
  // navigating from a board (DocumentPreview.getEditLink). Documents opened from
  // the Data Catalog have no param, so the header shows "Data Catalog" instead of
  // the (often stale "Master Board") active board in the store. Back-nav is
  // history-based (navigateBack), so it returns to wherever the user came from
  // regardless of this label.
  const boardName = searchParams.get('boardName') || null
  const [docName, setDocName] = useState<string>('')
  const [docLocation, setDocLocation] = useState<string>('')
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pageText, setPageText] = useState<string | null>(null)
  const [textError, setTextError] = useState<string | null>(null)
  const [documentMetadata, setDocumentMetadata] = useState<DocumentMetadata | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const [loadingPdf, setLoadingPdf] = useState(true)
  const [loadingText, setLoadingText] = useState(true)
  const [sidePanel, setSidePanel] = useState<'text' | null>('text')
  const [hasCompatibilityIssues, setHasCompatibilityIssues] = useState(false)
  const [activeTab, setActiveTab] = useState<DocumentTab>('review')

  // Track previous doc_id to detect navigation vs polling
  const prevDocIdRef = useRef(doc_id)

  // Page text cache for instant page switching (stores raw text and formatting state)
  const pageTextCacheRef = useRef<Map<number, { text: string | null; isFormatted: boolean }>>(new Map())

  // Track pages that have been edited to prevent prefetch from overwriting
  const editedPagesRef = useRef<Set<number>>(new Set())

  // Track current doc_id for prefetch race condition prevention
  const currentDocIdRef = useRef<string | undefined>(doc_id)

  // Track total pages for prefetching
  const [totalPages, setTotalPages] = useState<number>(0)

  // Mixed document support
  const [classifications, setClassifications] = useState<PageClassification[]>([])
  // Kept for pnid_graph_ids — the canonical page→graph map for a multi-page P&ID.
  const [docProperties, setDocProperties] = useState<{
    graph_id?: string
    pnid_graph_ids?: unknown
  } | null>(null)

  // Graph view support
  const [updatingClassification, setUpdatingClassification] = useState(false)

  // Get current page's classification and graph_id dynamically
  const currentPageClassification = classifications.find(c => c.page_number === currentPage)
  const currentPageType = currentPageClassification?.page_type || 'other'
  const currentPageGraphId = currentPageClassification?.metadata?.graph_id || null

  // Every P&ID page of this PDF that has a graph, as the editor's `pnidPages`
  // param. Without it the editor has no page map, so a multi-sheet drawing
  // opened from here lands on one sheet with no way to reach the rest — the
  // catalog passes the same param via graphEditorUrlForDocument.
  //
  // The document's own pnid_graph_ids is the canonical map and the source the
  // catalog reads. Per-page classification metadata is only a fallback: it is
  // written in one place (routes/mixed_documents.py) inside a vision-model
  // success branch, so on an automatically processed PDF some pages can carry
  // no graph_id at all and a map built from it alone would be short or empty.
  const pnidPagesParam = useMemo(
    () =>
      pnidPagesParamFor(
        docProperties,
        classifications
          .filter((c) => c.page_type === 'pnid' && c.metadata?.graph_id)
          .map((c) => ({ page: c.page_number, graphId: c.metadata!.graph_id! }))
      ),
    [docProperties, classifications]
  )

  const renameDocument = useRenameDocument()
  const handleRename = (newName: string) => {
    void renameDocument(doc_id, newName, { previousName: docName, applyName: setDocName })
  }

  // All documents are converted to PDF on backend, so no need to detect file type

  // Fetch page classifications for mixed document support
  useEffect(() => {
    const fetchClassifications = async () => {
      if (!doc_id) return

      try {
        const classData = await apiService.getPageClassifications(doc_id)
        setClassifications(classData)
      } catch {
        // Document not classified - silently fall back to regular viewer
      }
    }

    fetchClassifications()
  }, [doc_id])

  useEffect(() => {
    const loadDocument = async () => {
      if (!doc_id) return

      // Detect if this is a navigation to a new document
      const isNewDocument = prevDocIdRef.current !== doc_id
      prevDocIdRef.current = doc_id

      // Show loading state if:
      // 1. Navigating to a new document, OR
      // 2. Not currently in processing state (initial load or error recovery)
      if (isNewDocument || !isProcessing) {
        setLoadingPdf(true)
      }

      // UI-41 / greptile: `docName` is set from an async fetch below, so on a
      // navigation it still holds the PREVIOUS document's name until that
      // resolves. Page text can load first, and the raw export would then save
      // one document's text under another's name — worse than the UUID this
      // ticket replaced. Clearing it means the export falls back to the generic
      // "document" for that window: vague, but never wrong.
      if (isNewDocument) {
        setDocName('')
      }

      try {
        // Fetch document metadata first
        const doc = await apiService.getDocument(doc_id)
        // Clean legacy "P&ID Graph "/"PFD Graph " marker, and the bare
        // "P&ID "/"PFD " marker on pnid/pfd-typed rows, before display.
        setDocName(cleanDocumentName(doc.name, doc.type))
        setDocLocation(doc.location || '')
        setDocProperties(doc.properties ?? null)

        // Check if document location is still an Office format (not yet converted to PDF)
        const officeExtensions = ['.docx', '.doc', '.pptx', '.ppt', '.xlsx', '.xls']
        const locationLower = (doc.location || '').toLowerCase()
        const isStillProcessing = officeExtensions.some(ext => locationLower.endsWith(ext))

        if (isStillProcessing) {
          // Document is still being processed, show processing message
          setIsProcessing(true)
          setPdfUrl(null)
          setLoadingPdf(false)
          return
        }

        // Document is ready, download the PDF
        // Ensure loading state is shown while downloading to prevent flicker
        setLoadingPdf(true)
        setIsProcessing(false)
        const pdfBlob = await apiService.downloadDocument(doc_id)
        const url = URL.createObjectURL(pdfBlob)
        setPdfUrl(url)
        
        // Format file size
        const formatFileSize = (bytes: number) => {
          if (bytes < 1024) return bytes + ' B'
          if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
          return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
        }
        
        setDocumentMetadata({
          uploadedBy: doc.uploader_name || getUserDisplayName(doc.user_id, user),
          uploadDate: doc.created_at,
          lastModified: doc.updated_at || doc.created_at,
          fileSize: formatFileSize(pdfBlob.size),
          documentType: doc.type.charAt(0).toUpperCase() + doc.type.slice(1),
          category: doc.description || 'Uncategorized',
          extractionStatus: doc.is_indexed ? 'Completed' : 'Processing',
          extractionDate: doc.processing_metadata?.completed_indexing ? doc.updated_at : undefined,
        })

        trackEvent('document-viewer_loaded', {
          document_id: doc_id,
          document_type: doc.type,
        })
      } catch (error) {
        console.error('Error loading PDF:', error)
        setIsProcessing(false)
      } finally {
        setLoadingPdf(false)
      }
    }

    loadDocument()

    // Cleanup
    return () => {
      // Cleanup will happen in a separate effect
    }
  }, [doc_id, user, refreshTrigger])

  // Poll for document conversion completion when in processing state
  useEffect(() => {
    if (!isProcessing) return

    const pollInterval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1)
    }, 3000) // Check every 3 seconds

    return () => clearInterval(pollInterval)
  }, [isProcessing])

  // Cleanup PDF URL when it changes or component unmounts
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [pdfUrl])

  // Clear page text cache, edited pages, and update current doc ref when document changes
  useEffect(() => {
    currentDocIdRef.current = doc_id
    pageTextCacheRef.current.clear()
    editedPagesRef.current.clear()
  }, [doc_id])

  // Prefetch adjacent pages for instant navigation
  const prefetchAdjacentPages = async (page: number, docId: string, numPages: number) => {
    const pagesToPrefetch = [page - 1, page + 1].filter(
      p => p >= 1 && p <= numPages &&
           !pageTextCacheRef.current.has(p) &&
           !editedPagesRef.current.has(p)  // Skip edited pages to prevent data loss
    )

    for (const p of pagesToPrefetch) {
      try {
        const text = await apiService.getDocumentPageText(docId, p - 1)
        // Only cache if still viewing the same document, page wasn't edited, and not already cached
        // (re-check has(p) after await to avoid overwriting formatted entries from main effect)
        if (currentDocIdRef.current === docId && !editedPagesRef.current.has(p) && !pageTextCacheRef.current.has(p)) {
          // Evict oldest non-edited entry if cache is full (LRU eviction)
          if (pageTextCacheRef.current.size >= MAX_TEXT_CACHE_SIZE) {
            let evicted = false
            for (const key of pageTextCacheRef.current.keys()) {
              if (!editedPagesRef.current.has(key)) {
                pageTextCacheRef.current.delete(key)
                evicted = true
                break
              }
            }
            // Don't add to cache if we couldn't evict (all entries are edited)
            if (!evicted) continue
          }
          pageTextCacheRef.current.set(p, { text, isFormatted: false })
        }
      } catch {
        // Silently fail prefetch - not critical
      }
    }
  }

  // Load page text when page changes with caching for instant page switching
  useEffect(() => {
    if (!doc_id) return

    let isCancelled = false

    const loadPageText = async () => {
      // Check cache first for instant display
      const cached = pageTextCacheRef.current.get(currentPage)
      if (cached) {
        // Check if cancelled before modifying cache to prevent losing entries during rapid navigation
        if (isCancelled) return

        // Move to end of Map for true LRU (most recently used)
        pageTextCacheRef.current.delete(currentPage)

        // Only format if not already formatted (prevents double-formatting edited content)
        let displayText: string | null
        let updatedCached = cached
        if (cached.isFormatted) {
          displayText = cached.text
        } else {
          displayText = cached.text ? formatPageText(cached.text) : null
          // Update cache with formatted flag to avoid re-formatting on next visit
          updatedCached = { text: displayText, isFormatted: true }
        }
        pageTextCacheRef.current.set(currentPage, updatedCached)

        setPageText(displayText)
        setLoadingText(false)
        setTextError(null)

        // Still prefetch adjacent pages in background
        if (totalPages > 0) {
          prefetchAdjacentPages(currentPage, doc_id, totalPages)
        }
        return
      }

      setLoadingText(true)
      setTextError(null)

      try {
        if (isCancelled) return

        const textContent = await apiService.getDocumentPageText(
          doc_id,
          currentPage - 1
        )

        // Only update state if this request hasn't been cancelled
        if (!isCancelled) {
          // Evict oldest non-edited entry if cache is full (LRU eviction)
          let canCache = true
          if (pageTextCacheRef.current.size >= MAX_TEXT_CACHE_SIZE) {
            let evicted = false
            for (const key of pageTextCacheRef.current.keys()) {
              if (!editedPagesRef.current.has(key)) {
                pageTextCacheRef.current.delete(key)
                evicted = true
                break
              }
            }
            // Don't cache if we couldn't evict (all entries are edited)
            canCache = evicted
          }
          // Store raw text in cache for future instant access
          if (canCache) {
            pageTextCacheRef.current.set(currentPage, { text: textContent, isFormatted: false })
          }
          setPageText(textContent ? formatPageText(textContent) : null)
          setTextError(null)

          // Prefetch adjacent pages in background
          if (totalPages > 0) {
            prefetchAdjacentPages(currentPage, doc_id, totalPages)
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error(
            `Error loading page text for page ${currentPage}:`,
            error
          )
          setPageText(null)
          setTextError(t('document_viewer:pdf.textLoadError', { page: currentPage, defaultValue: 'Failed to load text for page {{page}}' }))
        }
      } finally {
        if (!isCancelled) {
          setLoadingText(false)
        }
      }
    }

    loadPageText()

    return () => {
      isCancelled = true
    }
  }, [doc_id, currentPage, totalPages])

  // All documents are converted to PDF on backend

  // Handle page synchronization
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  // Handle visual element click - navigate to specific page
  const handleVisualElementClick = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  // Handle content update from HITL interface
  const handleContentUpdate = (newContent: string, pageNumber: number) => {
    // Only update displayed text if still viewing the edited page
    if (pageNumber === currentPage) {
      setPageText(newContent)
    }
    // Mark page as edited to prevent prefetch from overwriting
    editedPagesRef.current.add(pageNumber)
    // Store as already formatted to prevent double-formatting on retrieval
    pageTextCacheRef.current.set(pageNumber, { text: newContent, isFormatted: true })
  }

  // Handle page classification update (mark/unmark as P&ID)
  const handleUpdateClassification = async (newPageType: 'pnid' | 'other') => {
    if (!doc_id) {
      console.error('No document ID available')
      return
    }

    setUpdatingClassification(true)
    try {
      const response = await apiService.updatePageClassification(
        doc_id,
        currentPage,
        newPageType
      )

      if (response.success) {
        // Refresh classifications to get updated data
        const classData = await apiService.getPageClassifications(doc_id)
        setClassifications(classData)
      }
    } catch (error) {
      console.error('Failed to update page classification:', error)
    } finally {
      setUpdatingClassification(false)
    }
  }

  // Create actions array for BoardLayout header
  // Show button for P&ID pages, but disable if graph is not ready yet
  const pnidAction = currentPageType === 'pnid' ? [
    {
      onClick: () => {
        if (currentPageGraphId && doc_id) {
          // Opens the page the user is looking at, not page 1 — `page` stays as
          // the fallback the editor reads when there is no page map.
          // doc_id is guarded above: useParams types it optional, and the old
          // template literal would have interpolated a literal "undefined".
          const params = new URLSearchParams({ documentId: doc_id, page: String(currentPage) })
          if (pnidPagesParam) params.set('pnidPages', pnidPagesParam)
          navigate(`/graph-editor/${currentPageGraphId}?${params.toString()}`)
        }
      },
      element: (
        <button
          type="button"
          disabled={!currentPageGraphId}
          className={`px-4 py-2 text-sm font-medium rounded-lg border flex items-center gap-2 ${
            currentPageGraphId
              ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 cursor-pointer'
              : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 cursor-not-allowed'
          }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0v10" />
          </svg>
          {currentPageGraphId
            ? t('document_viewer:pdf.openGraphEditor', { defaultValue: 'Open P&ID Graph Editor' })
            : t('document_viewer:pdf.graphProcessing', { defaultValue: 'P&ID Graph Processing...' })}
        </button>
      ),
    }
  ] : []

  // Close button action - always shown
  const closeAction = {
    onClick: navigateBack,
    element: (
      <button
        type="button"
        className="p-2 text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-accent rounded-lg transition-colors"
        title={t('common:buttons.close')}
      >
        <X className="w-5 h-5" />
      </button>
    ),
  }

  const boardActions = [...pnidAction, closeAction]

  // Security: in embed mode, block access to documents not in the allowed list.
  // allowedDocumentIds is [] by default in embed mode (closed), only populated after BOARD_CONTEXT.
  if (isEmbedMode && allowedDocumentIds !== null && doc_id && !allowedDocumentIds.includes(doc_id)) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-50 dark:bg-dark-muted">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-dark-text mb-2">{t('document_viewer:pdf.accessDenied', { defaultValue: 'Access Denied' })}</h2>
          <p className="text-gray-600 dark:text-gray-400">{t('document_viewer:pdf.accessDeniedDescription', { defaultValue: 'This document is not available in the current context.' })}</p>
        </div>
      </div>
    )
  }

  // Content shared between embed and non-embed modes
  const viewerContent = (
    <div className="w-full h-full overflow-hidden">
      <AdBlockerWarning
        onDetectionComplete={setHasCompatibilityIssues}
        showToast={true}
        showInline={hasCompatibilityIssues}
      />
      <div className="flex flex-col h-full gap-0">
        {docName && (
          <div className="shrink-0 px-4 py-2 border-b border-gray-200 dark:border-dark-border">
            <FileNameWithCopy name={docName} onRename={handleRename} />
          </div>
        )}
        <div className="flex-1 w-full flex gap-0 min-h-0">
          {/* PDF Viewer - Dynamic width based on side panel state */}
          <div className={sidePanel ? 'w-1/2 border-r' : 'w-full'}>
            <div className="h-full overflow-hidden">
              {loadingPdf ? (
                <div className="h-full flex flex-col items-center justify-center gap-2">
                  <svg className="animate-spin h-6 w-6 text-gray-400 dark:text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {docName && (
                    <div className="text-gray-500 dark:text-dark-text-muted text-sm">
                      {t('pdf_viewer.status.loading_file', { fileType: getFileExtension(docName) })}
                    </div>
                  )}
                </div>
              ) : isProcessing ? (
                <div className="h-full flex flex-col items-center justify-center gap-3">
                  <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <div className="text-gray-700 dark:text-gray-300 text-base">
                    {t('pdf_viewer.status.processing')}
                  </div>
                </div>
              ) : (
                <PdfJsViewer
                  url={pdfUrl}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onTotalPagesChange={setTotalPages}
                  documentName={docName}
                  documentLocation={docLocation}
                  documentId={doc_id}
                />
              )}
            </div>
          </div>

          {/* Tabbed Content Panel - Text/Extractions/Raw/Summary/Details */}
          {sidePanel === 'text' && (
            <div className="w-1/2 flex flex-col h-full overflow-hidden bg-gray-50 dark:bg-dark-muted">
              <DocumentTabs activeTab={activeTab} onTabChange={setActiveTab} />
              {loadingText ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-gray-500 dark:text-dark-text-muted">
                    {t('document_viewer:pdf.loadingPageText', { page: currentPage, defaultValue: 'Loading page {{page}} text content...' })}
                  </div>
                </div>
              ) : textError ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-red-500 text-center p-4">
                    <div>{textError}</div>
                    <button
                      onClick={() => window.location.reload()}
                      className="mt-2 px-3 py-1 text-sm bg-gray-100 dark:bg-dark-accent border rounded hover:bg-gray-200 dark:hover:bg-dark-muted"
                    >
                      {t('document_viewer:pdf.retry', { defaultValue: 'Retry' })}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-hidden">
                  {activeTab === 'review' && (
                    <ReviewTab
                      pageText={pageText}
                      docId={doc_id}
                      currentPage={currentPage}
                      onContentUpdate={handleContentUpdate}
                    />
                  )}
                  {activeTab === 'extractions' && doc_id && (
                    <ExtractionsTab
                      pageText={pageText}
                      documentId={doc_id}
                      onElementClick={handleVisualElementClick}
                    />
                  )}
                  {activeTab === 'raw' && doc_id && (
                    <RawTab
                      pageText={pageText}
                      fileName={docName}
                    />
                  )}
                  {activeTab === 'summary' && (
                    <SummaryTab
                      docName={docName}
                      pageText={pageText}
                      docId={doc_id}
                      currentPage={currentPage}
                    />
                  )}
                  {activeTab === 'details' && (
                    <DetailsTab
                      docName={docName}
                      documentMetadata={documentMetadata ?? undefined}
                    />
                  )}
                  {IO_LINE_TRACING_ENABLED && activeTab === 'lines' && doc_id && (
                    <LinesTab documentId={doc_id} />
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom controls - hidden in embed mode */}
        {!isEmbedMode && (
          <div className="w-full px-4 py-3 border-t bg-white dark:bg-dark-card flex justify-between items-center">
            {/* Page Classification Controls (only show if classifications available) */}
            {currentPageClassification && (
                <div className="flex gap-2 items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('document_viewer:pdf.currentPage', { defaultValue: 'Current Page:' })}</span>
                  <div className="inline-flex rounded-md shadow-sm" role="group">
                    <button
                      type="button"
                      onClick={() => handleUpdateClassification('pnid')}
                      disabled={updatingClassification || currentPageType === 'pnid'}
                      className={`px-3 py-2 text-sm font-medium rounded-l border ${
                        currentPageType === 'pnid'
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-white dark:bg-dark-card text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-dark-muted disabled:opacity-50'
                      }`}
                    >
                      {currentPageType === 'pnid'
                        ? t('document_viewer:pdf.pnidMarked', { defaultValue: '✓ P&ID' })
                        : t('document_viewer:pdf.markAsPnid', { defaultValue: 'Mark as P&ID' })}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateClassification('other')}
                      disabled={updatingClassification || currentPageType === 'other'}
                      className={`px-3 py-2 text-sm font-medium rounded-r border ${
                        currentPageType === 'other'
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-white dark:bg-dark-card text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-dark-muted disabled:opacity-50'
                      }`}
                    >
                      {currentPageType === 'other'
                        ? t('document_viewer:pdf.textMarked', { defaultValue: '✓ Text' })
                        : t('document_viewer:pdf.markAsText', { defaultValue: 'Mark as Text' })}
                    </button>
                  </div>
                  {updatingClassification && (
                    <span className="text-sm text-gray-500 dark:text-dark-text-muted">{t('document_viewer:pdf.updating', { defaultValue: 'Updating...' })}</span>
                  )}
                </div>
              )}

            {/* Toggle buttons */}
            <div className="flex gap-2">
            <button
              onClick={() => setSidePanel(sidePanel === 'text' ? null : 'text')}
              className={`px-4 py-2 text-sm border rounded hover:bg-gray-100 dark:hover:bg-dark-accent flex items-center gap-2 ${
                sidePanel === 'text' ? 'bg-gray-100 dark:bg-dark-accent text-gray-900 dark:text-dark-text border-gray-300 dark:border-gray-600' : 'bg-white dark:bg-dark-card text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
              }`}
            >
              {sidePanel === 'text'
                ? t('pdf_viewer.actions.hide_text')
                : t('pdf_viewer.actions.show_text')}
            </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  // In embed mode, render without BoardLayout header
  if (isEmbedMode) {
    return viewerContent
  }

  return (
    <BoardLayout
      name={boardName || t('nav:menu.data', 'Data')}
      backUrl={''}
      handleBackCallback={navigateBack}
      actions={boardActions}
    >
      {viewerContent}
    </BoardLayout>
  )
}
